/**
 * Banner "Sessione in scadenza": pannello fisso in alto, centrato, non
 * bloccante. Compare solo nella finestra di avviso dello store `session-idle`
 * e sparisce da solo quando la deadline si sposta (attività in qualunque tab).
 *
 * "Mantieni sessione" = attività utente esplicita (`store.touch()`), più la
 * callback opzionale dell'adapter (es. rinnovo JWT immediato). "Esci" delega
 * all'adapter il logout esplicito.
 */
import * as React from 'react';
import { Clock, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { getSessionIdleStore } from '../../lib/session-idle/store';
import type { SessionIdleStore, SessionScope } from '../../lib/session-idle/types';
import { useSessionCountdown } from '../../hooks/useSessionCountdown';

export interface SessionTimeoutBannerProps {
  readonly scope: SessionScope;
  /** Anticipo della finestra di avviso (ms). Default: `warnBeforeMs` della config dello store (120 000 se non impostato). */
  readonly warnBeforeMs?: number;
  /** Dopo `touch()`: es. forzare un refresh JWT. */
  readonly onKeepAlive?: () => void | Promise<unknown>;
  /** Logout esplicito dell'utente. */
  readonly onLogout: () => void | Promise<unknown>;
  readonly title?: string;
  /** Testo prima del countdown; il countdown `m:ss` viene aggiunto dal componente. */
  readonly description?: string;
  readonly keepAliveLabel?: string;
  readonly logoutLabel?: string;
  readonly className?: string;
  /** Store alternativo (test); default: singleton dello scope. */
  readonly store?: SessionIdleStore;
}

export function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function SessionTimeoutBanner(props: SessionTimeoutBannerProps): React.ReactElement | null {
  const {
    scope,
    warnBeforeMs,
    onKeepAlive,
    onLogout,
    title = 'Sessione in scadenza',
    description = 'Per inattività la sessione terminerà tra',
    keepAliveLabel = 'Mantieni sessione',
    logoutLabel = 'Esci',
    className,
  } = props;
  const store = React.useMemo(() => props.store ?? getSessionIdleStore(scope), [props.store, scope]);
  const { remainingMs, isWarning } = useSessionCountdown({ scope, warnBeforeMs, store });
  const [busy, setBusy] = React.useState(false);
  const titleId = React.useId();
  const descriptionId = React.useId();

  const handleKeepAlive = React.useCallback(async () => {
    store.touch();
    // touch() su deadline già superata (tab sospesa) fa scadere la sessione:
    // in quel caso NESSUN rinnovo credenziali deve partire.
    if (!onKeepAlive || store.getStatus() !== 'running') return;
    setBusy(true);
    try {
      await onKeepAlive();
    } finally {
      setBusy(false);
    }
  }, [store, onKeepAlive]);

  const handleLogout = React.useCallback(async () => {
    setBusy(true);
    try {
      await onLogout();
    } finally {
      setBusy(false);
    }
  }, [onLogout]);

  if (!isWarning) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      data-testid="session-timeout-banner"
      className={cn(
        'fixed inset-x-0 top-4 z-[100] mx-auto w-[calc(100%-2rem)] max-w-md',
        'rounded-xl border border-amber-200 bg-amber-50 text-amber-900 shadow-lg',
        'dark:border-amber-900/50 dark:bg-amber-950 dark:text-amber-200',
        className,
      )}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <Clock className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="text-sm font-semibold">
            {title}
          </h2>
          <p id={descriptionId} className="mt-0.5 text-sm" aria-live="polite" aria-atomic="true">
            {description}{' '}
            <span className="font-mono font-semibold tabular-nums" data-testid="session-timeout-countdown">
              {formatCountdown(remainingMs)}
            </span>
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" onClick={() => void handleKeepAlive()} disabled={busy}>
              {keepAliveLabel}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => void handleLogout()}
              disabled={busy}
              className="text-amber-800 hover:text-amber-950 dark:text-amber-200 dark:hover:text-amber-50"
            >
              <LogOut className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {logoutLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
